import { useState, useRef, useEffect } from "react";
import emailjs from "@emailjs/browser";

export default function EnquiryForm() {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    practice: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Initialize EmailJS with your Public Key from Account > API Keys
  useEffect(() => {
    emailjs.init("0efh29XDRVfrvpH7a"); // Get from: Account > API Keys
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePracticeSelect = (practiceValue) => {
    setFormData((prev) => ({ ...prev, practice: practiceValue }));
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current || loading) {
      console.log("Form already submitting, ignoring duplicate request");
      return;
    }

    if (!formData.fullName.trim()) {
      setMessage("Please enter your full name.");
      return;
    }
    if (!formData.phone.trim()) {
      setMessage("Please enter your phone number.");
      return;
    }
    if (!formData.practice.trim()) {
      setMessage("Please select a practice area.");
      return;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s-]/g, ""))) {
      setMessage(
        "Please enter a valid 10-digit phone number (starts with 6-9)."
      );
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setMessage("");

    try {
      const templateParams = {
        fullName: formData.fullName.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || "Not provided",
        practice: formData.practice.trim(),
        to_email: "blackthornchambers@gmail.com",
        timestamp: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      await emailjs.send(
        "service_2aewgq9",      // Get from: Email Services (e.g., service_2aewgq9)
        "template_1gz1f05",     // Get from: Email Templates > Settings (e.g., template_eadc4fi)
        templateParams
      );

      console.log("✅ Email sent successfully");

      setMessage(
        `✅ Enquiry sent successfully! Our expert will contact you within 24 hours on +91 ${formData.phone}`
      );
      
      setFormData({ fullName: "", phone: "", email: "", practice: "" });
      setOpen(false);
    } catch (error) {
      console.error("EmailJS Error:", error);
      setMessage(
        "❌ Failed to send enquiry. Please try again or WhatsApp +91-7303269472."
      );
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const options = [
    "Corporate & Commercial Laws",
    "Mergers, Acquisitions & Joint Ventures",
    "Corporate Governance & Compliance",
    "Startup Advisory & Business Structuring",
    "Civil Litigation & Commercial Disputes",
    "Criminal Law & Defense",
    "White-Collar Crimes & Economic Offences",
    "Enforcement Directorate (ED) Matters",
    "Central Bureau of Investigation (CBI) Matters",
    "Arbitration, Mediation & Alternative Dispute Resolution (ADR)",
    "International & Cross-Border Disputes",
    "Banking & Finance Laws",
    "Insolvency & Bankruptcy Code (IBC) Matters",
    "Project Finance & Infrastructure Laws",
    "Goods and Services Tax (GST) Matters",
    "Income Tax & Direct Tax Matters",
    "Customs, Excise & International Taxation",
    "Real Estate & Property Laws",
    "Land Acquisition & Title Due Diligence",
    "Labour & Employment Laws",
    "Service Law & Government Employment Matters",
    "Family & Matrimonial Laws",
    "Succession, Inheritance & Estate Planning",
    "Intellectual Property Rights (IPR)",
    "Information Technology & Cyber Laws",
    "Data Protection & Privacy Laws",
    "Consumer Protection Laws",
    "Constitutional & Administrative Law",
    "Environmental Laws",
    "Regulatory & Compliance Services",
    "Legal Retainership & Ongoing Advisory",
    "Notarial & Documentation Services",
    "Trust, Society & NGO Laws",
    "Media, Entertainment & Sports Laws",
    "Insurance & Medical Negligence Laws",
    "Election & Political Law Advisory",
    "Other (Please Specify)"
  ];

  useEffect(() => {
    const handler = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="w-full space-y-3 sm:space-y-4 md:space-y-5 px-3 py-4 sm:px-4 sm:py-5 md:px-0 md:py-0">
      {/* FORM TITLE */}
      <div className="text-center mb-3 sm:mb-4">
        <h3 className="text-xl sm:text-2xl md:text-3xl font-playfair font-bold text-[#123a6f] mb-1.5 sm:mb-2">
          Get Legal Consultation
        </h3>
        <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed max-w-md mx-auto px-2 sm:px-0">
          Fill in your details and our experts will reach out to you.
        </p>
      </div>

      {/* FORM */}
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        {/* Full Name */}
        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-1.5">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
            disabled={loading}
            placeholder="Enter your full name"
            className="w-full px-3 py-2 sm:px-4 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border-2 border-gray-200 bg-white text-sm sm:text-base font-semibold focus:border-[#123a6f] focus:ring-2 focus:ring-[#123a6f]/20 outline-none transition-all duration-300 hover:border-yellow-400 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Phone Number */}
        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-1.5">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            disabled={loading}
            placeholder="Enter 10-digit mobile number"
            maxLength={10}
            className="w-full px-3 py-2 sm:px-4 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border-2 border-gray-200 bg-white text-sm sm:text-base font-semibold focus:border-[#123a6f] focus:ring-2 focus:ring-[#123a6f]/20 outline-none transition-all duration-300 hover:border-yellow-400 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-1.5">
            Email Address (Optional)
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            placeholder="your.email@example.com"
            className="w-full px-3 py-2 sm:px-4 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border-2 border-gray-200 bg-white text-sm sm:text-base font-semibold focus:border-[#123a6f] focus:ring-2 focus:ring-[#123a6f]/20 outline-none transition-all duration-300 hover:border-yellow-400 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* PRACTICE AREA */}
        <PracticeSelect
          selectRef={selectRef}
          open={open}
          setOpen={setOpen}
          value={formData.practice}
          onSelect={handlePracticeSelect}
          options={options}
          disabled={loading}
        />

        {/* SUCCESS/ERROR MESSAGE */}
        {message && (
          <div
            className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg text-xs sm:text-sm font-semibold text-center transition-all duration-300 ${
              message.includes("successfully")
                ? "bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-800 border-2 border-emerald-200 animate-pulse"
                : "bg-gradient-to-r from-rose-50 to-red-50 text-rose-800 border-2 border-rose-200"
            }`}
          >
            {message}
          </div>
        )}

        {/* SUBMIT BUTTON */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-yellow-500 text-white py-2.5 sm:py-3 md:py-3.5 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-lg font-bold shadow-xl hover:bg-yellow-600 hover:shadow-2xl hover:-translate-y-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all duration-300 active:scale-95"
        >
          {loading ? (
            <>
              <svg
                className="animate-spin inline mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting...
            </>
          ) : (
            "Submit"
          )}
        </button>

        <div className="mt-3 sm:mt-4">
          <a
            href="https://wa.me/917303269472"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-[#25D366] text-white py-2.5 sm:py-3 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-lg font-bold shadow-xl hover:bg-[#16bc53] hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 active:scale-95 flex items-center justify-center gap-2"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 32 32"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              className="text-white w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 group-hover:scale-110 transition-transform duration-300"
            >
              <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.244-.73.244-1.088 0-.058 0-.144-.03-.215-.1-.172-2.434-1.39-2.678-1.39zm-2.908 7.593c-1.747 0-3.48-.53-4.942-1.49L7.793 24.41l1.132-3.337a8.955 8.955 0 0 1-1.72-5.272c0-4.955 4.04-8.995 8.997-8.995S25.2 10.845 25.2 15.8c0 4.958-4.04 8.998-8.998 8.998zm0-19.798c-5.96 0-10.8 4.842-10.8 10.8 0 1.964.53 3.898 1.546 5.574L5 27.176l5.974-1.92a10.807 10.807 0 0 0 16.03-9.455c0-5.958-4.842-10.8-10.802-10.8z" />
            </svg>
            <span className="text-sm sm:text-base">Chat on WhatsApp</span>
          </a>
        </div>
      </form>

      {/* TRUST BADGE */}
      <div className="text-center pt-2 sm:pt-3 border-t border-gray-200">
        <p className="text-[10px] sm:text-xs text-black font-medium">
          🔒 Secure. Encrypted. Confidential.
        </p>
      </div>
    </div>
  );
}

function PracticeSelect({
  selectRef,
  open,
  setOpen,
  value,
  onSelect,
  options,
  disabled = false,
}) {
  return (
    <div ref={selectRef}>
      <label className="block text-xs sm:text-sm font-bold text-gray-800 mb-1 sm:mb-1.5">
        Case Type <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
          className="w-full px-3 py-2 sm:px-4 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border-2 border-gray-200
                    bg-white text-xs sm:text-sm md:text-base font-semibold text-gray-800 text-left
                    hover:border-yellow-500
                    focus:border-[#123a6f] focus:ring-2 focus:ring-[#123a6f]/20
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all duration-300"
        >
          <span className="truncate block pr-8">
            {value || "Select your Case Type"}
          </span>
          <svg
            className={`absolute right-2 sm:right-3 md:right-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {open && !disabled && (
          <ul className="absolute z-50 bottom-full mb-1 sm:mb-2 w-full
                      rounded-lg sm:rounded-xl shadow-xl
                      border border-gray-200 bg-white max-h-48 sm:max-h-60 overflow-y-auto">
            {options.map((item, index) => (
              <li
                key={item}
                onClick={() => onSelect(item)}
                className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-800
                               cursor-pointer
                               hover:bg-yellow-500 hover:text-[#123a6f]
                               transition duration-300 hover:bg-gradient-to-r ${
                  index === 0 ? "rounded-t-lg sm:rounded-t-xl" : ""
                } ${
                  index === options.length - 1 ? "rounded-b-lg sm:rounded-b-xl" : ""
                }`}
              >
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
